import { ChatMessageModel } from '../client/SmartAssistantBackendClient';
import { extractContentText, normalizeMessages, unwrapMsgEnvelope } from './chatMessageUtils';

type MergeOptions = {
  shouldProcessPartial?: () => boolean;
};

export function mergeSignalRMessages(
  current: ChatMessageModel[],
  payload: any,
  eventName?: string,
  options: MergeOptions = {},
): ChatMessageModel[] {
  const appendOrUpdateAssistant = (text: string) => {
    const last = current[current.length - 1];
    if (!last || last.role !== 'assistant') {
      console.log('[Merge][SignalR] added new message');
      return [...current, ChatMessageModel.fromJS({ role: 'assistant', content: text })];
    }
    if (last.role === 'assistant') {
      const merged = updateLastAssistant(item => ChatMessageModel.fromJS({
        ...item,
        role: 'assistant',
        content: `${item.content ?? ''}${text}`,
      }));
      if (merged) {
        console.log('[Merge][SignalR] appended to existing message');
        return merged;
      }
    }
    return current;
  };

  const replaceOrAppendFinalAssistant = (text: string) => {
    const preview = (value: string) => {
      const clean = value ?? '';
      const head = clean.slice(0, 120);
      const tail = clean.length > 240 ? clean.slice(-120) : '';
      return { length: clean.length, head, tail };
    };
    // Skip trailing empty user messages (e.g., clarification-only submits).
    let idx = current.length - 1;
    while (idx >= 0) {
      const item = current[idx];
      const isEmptyUser = item?.role === 'user'
        && typeof item?.content === 'string'
        && item.content.trim().length === 0;
      if (isEmptyUser) {
        idx -= 1;
        continue;
      }
      break;
    }
    const candidate = idx >= 0 ? current[idx] : undefined;
    if (candidate?.role === 'assistant') {
      const next = [...current];
      next[idx] = ChatMessageModel.fromJS({
        ...candidate,
        role: 'assistant',
        content: text,
      });
      console.log('[Merge][SignalR] replaced message', preview(text));
      return next;
    }
    return appendOrUpdateAssistant(text);
  };

  const updateLastAssistant = (updater: (item: ChatMessageModel) => ChatMessageModel) => {
    const idx = current.length - 1;
    const item = current[idx];
    if (item?.role !== 'assistant') return null;
    const next = [...current];
    next[idx] = updater(item);
    return next;
  };

  let data = unwrapMsgEnvelope(payload);
  if (typeof payload === 'string') {
    try {
      data = unwrapMsgEnvelope(JSON.parse(payload));
    } catch {
      const isPartialEvent = eventName?.toLowerCase().includes('partial');
      if (isPartialEvent) {
        return appendOrUpdateAssistant(payload);
      }
      return appendOrUpdateAssistant(payload);
    }
  }

  if (data && typeof data === 'object') {
    const inferredPartial = eventName?.toLowerCase().includes('partial') || data?.isPartial === true || data?.type === 'partial';
    if (inferredPartial && !data.role) {
      data = { ...data, role: 'assistant' };
    }
  }

  if (data?.history && Array.isArray(data.history)) {
    return normalizeMessages(data.history);
  }

  if (Array.isArray(data)) {
    return normalizeMessages(data);
  }

  if (data?.currentTurnMessages && Array.isArray(data.currentTurnMessages)) {
    return [...current, ...normalizeMessages(data.currentTurnMessages)];
  }

  const eventNameLower = eventName?.toLowerCase();
  const isAssistantResponseEvent = eventNameLower?.includes('assistantresponse');
  const isPartialEvent = eventNameLower?.includes('partial') || data?.isPartial === true || data?.type === 'partial';
  const isFinalEvent = data?.isFinal === true
    || data?.type === 'final'
    || (isAssistantResponseEvent && data?.type !== 'partial');
  if (data?.content && isFinalEvent) {
    const text = extractContentText(data.content);
    if (text.trim().length === 0) {
      return current;
    }
    return replaceOrAppendFinalAssistant(text);
  }

  if (data?.content && isAssistantResponseEvent) {
    const text = extractContentText(data.content);
    if (text.trim().length === 0) {
      return current;
    }
    return replaceOrAppendFinalAssistant(text);
  }

  if (data?.content && isPartialEvent) {
    if (options.shouldProcessPartial && !options.shouldProcessPartial()) {
      return current;
    }
    const text = extractContentText(data.content);
    return appendOrUpdateAssistant(text);
  }

  if (data?.role || data?.content) {
    const contentText = extractContentText(data.content ?? data?.delta ?? data);
    if (data?.role === 'assistant' && contentText) {
      const merged = updateLastAssistant(item => ChatMessageModel.fromJS({
        ...item,
        content: `${item.content ?? ''}${contentText}`,
      }));
      if (merged) return merged;
    }
    const incoming = ChatMessageModel.fromJS({ ...data, content: contentText });
    return [...current, incoming];
  }

  console.warn('Unhandled SignalR payload', { eventName, data });
  return current;
}
